import { useMemo, useState } from 'react';
import type { NostrEvent } from '@nostrify/nostrify';
import { verifyEvent } from 'nostr-tools';
import { Check, Copy, Download, FileJson, ShieldAlert, ShieldCheck } from 'lucide-react';
import { useToast } from '@/hooks/useToast';
import { cn } from '@/lib/utils';

interface OriginalRecordProps {
  event: NostrEvent;
  /** The record's NIP-19 id, used for the downloaded file's name. */
  noteId: string;
}

/**
 * "View original": the record exactly as it exists on the network, as the
 * signed event itself. Anyone can check that what the page shows is what was
 * published, and take a copy away. Collapsed by default; the signature check
 * runs the moment it opens.
 */
export function OriginalRecord({ event, noteId }: OriginalRecordProps) {
  const [open, setOpen] = useState(false);
  const [copied, setCopied] = useState(false);
  const { toast } = useToast();

  // The exact bytes, pretty-printed. Only the standard fields, in the standard
  // order, so the text matches what a relay would return.
  const json = useMemo(() => {
    const { id, pubkey, created_at, kind, tags, content, sig } = event;
    return JSON.stringify({ id, pubkey, created_at, kind, tags, content, sig }, null, 2);
  }, [event]);

  // Checked here, in the reader's own browser: the id is the hash of the
  // record, and the signature is the archive key's over that hash.
  const valid = useMemo(() => {
    try {
      return verifyEvent(event);
    } catch {
      return false;
    }
  }, [event]);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(json);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      toast({ title: 'Could not copy', description: 'Select the text and copy it by hand.' });
    }
  };

  const download = () => {
    const blob = new Blob([json], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${noteId}.json`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const action =
    'inline-flex h-9 items-center gap-1.5 rounded-full border border-foreground/30 px-3.5 font-engraved text-[10px] font-semibold uppercase tracking-[0.18em] text-foreground transition-colors hover:bg-foreground hover:text-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1';

  return (
    <div className="mt-5 border-t border-foreground/15 pt-4">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
        aria-controls="original-record"
        className="flex w-full items-center justify-between gap-3 rounded-sm py-1 text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
      >
        <span className="flex items-center gap-2 font-engraved text-[10px] font-semibold uppercase tracking-[0.3em] text-foreground/70">
          <FileJson aria-hidden="true" className="h-3.5 w-3.5" />
          View original
        </span>
        <span
          className={cn(
            'inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 font-engraved text-[9px] font-bold uppercase tracking-[0.16em]',
            valid ? 'bg-foreground/[0.07] text-foreground' : 'bg-flag-red/10 text-flag-red',
          )}
        >
          {valid ? (
            <ShieldCheck aria-hidden="true" className="h-3.5 w-3.5" />
          ) : (
            <ShieldAlert aria-hidden="true" className="h-3.5 w-3.5" />
          )}
          {valid ? 'Signature verified' : 'Signature not verified'}
        </span>
      </button>

      {open && (
        <div id="original-record" className="mt-3">
          <p className="mb-3 font-display text-sm italic leading-snug text-foreground/70">
            The record as published: the signed event itself, in the form any Nostr relay
            holds it. The id is the fingerprint of these exact contents, and the signature is
            the archive key&rsquo;s mark over that fingerprint.
            {valid
              ? ' Both were checked in your browser just now, and they hold.'
              : ' The check could not be completed for this copy.'}
          </p>

          <pre className="max-h-80 overflow-auto rounded-2xl border border-foreground/20 bg-card p-4 font-mono text-[11px] leading-relaxed text-foreground">
            {json}
          </pre>

          <div className="mt-3 flex flex-wrap gap-2">
            <button type="button" onClick={copy} className={action}>
              {copied ? (
                <Check aria-hidden="true" className="h-3.5 w-3.5" />
              ) : (
                <Copy aria-hidden="true" className="h-3.5 w-3.5" />
              )}
              {copied ? 'Copied' : 'Copy'}
            </button>
            <button type="button" onClick={download} className={action}>
              <Download aria-hidden="true" className="h-3.5 w-3.5" />
              Download .json
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
